import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { ArrowRight } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, H1 } from '@liveauctioneers/hammer-ui-core/text';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { ChangeEvent } from 'react';

import { useValidateBidderEmail } from '@/components/Modals/DeleteAccountConfirm/hooks/useValidateBidderEmail';
import { getHomeUrl } from '@/utils/urls';

type StatusMessageProps = {
    email?: string;
    isAppleReauthError?: boolean;
    onCtaClick?: () => void;
    showHomePageLink?: boolean;
    subtitleId: string;
    subtitleValues?: Record<string, JSX.Element>;
    titleId: string;
};

const StatusMessage = ({
    email,
    isAppleReauthError = false,
    onCtaClick,
    showHomePageLink = true,
    subtitleId,
    subtitleValues,
    titleId,
}: StatusMessageProps) => {
    const { formatMessage } = useIntl();
    const { emailAddress, emailErrorText, isBidderEmail, isValidEmail, setEmailAddress, validateEmail } =
        useValidateBidderEmail(email);

    return (
        <div className="flex w-full flex-col gap-md smMin:max-w-[768px]">
            <H1>
                <FormattedMessage id={titleId} />
            </H1>
            <BodyPrimary>
                <FormattedMessage
                    id={subtitleId}
                    values={subtitleValues}
                />
            </BodyPrimary>
            {!isAppleReauthError && (
                <>
                    {showHomePageLink ? (
                        <SubtleLink
                            className="flex items-center gap-xs"
                            href={getHomeUrl()}
                        >
                            <FormattedMessage id="deleteAccountConfirmation.success.cta" />
                            <ArrowRight size="sm" />
                        </SubtleLink>
                    ) : (
                        <>
                            <TextInput
                                errorText={emailErrorText}
                                hasError={!isValidEmail || !isBidderEmail}
                                labelText={formatMessage({ id: 'deleteAccountModal.email.confirmAddress' })}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const value = e.target.value;
                                    setEmailAddress(value);
                                    validateEmail(value);
                                }}
                                type="email"
                                value={emailAddress}
                            />
                            <Button
                                className="w-full smMin:max-w-min"
                                disabled={!isValidEmail || !isBidderEmail}
                                onClick={onCtaClick}
                                variant="outlined"
                            >
                                <FormattedMessage id="deleteAccountConfirmation.requestAnotherEmail" />
                            </Button>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default StatusMessage;
